import { BidderSeat, BidderSeatType, CreateBidderSeat } from '@pubstack/common/src/bidder-seat'
import { sortByName } from '@pubstack/common/src/sort'
import { Product } from '~/hooks/useProduct'
import { buildUrlWithSearchParams } from '~/utils/url'
import { MutationToasts, useMutation, useQuery } from './api-client'

export const BIDDER_SEATS_QUERY_KEYS = ['bidder-seats']

/** One empty list for every render without data, so a memo over a disabled query holds. */
const NO_BIDDER_SEATS: BidderSeat[] = []

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
/**
 * `enabled` matters for `isInternal: true` specifically: that listing is Pubstack-only and 403s for a
 * customer, so a page carrying an internal tab has to hold the query back rather than fire it and
 * swallow the error. Same shape as `useCatalog`.
 */
export const useBidderSeats = (product: Product, queryParams?: { isInternal?: boolean; types?: BidderSeatType[]; name?: string }, enabled = true) => {
  const types = queryParams?.types?.join(',')
  const url = buildUrlWithSearchParams(`/${product}/bidder-seats`, { isInternal: queryParams?.isInternal, types, name: queryParams?.name })
  const query = useQuery<BidderSeat[]>({ url, keys: [product, ...BIDDER_SEATS_QUERY_KEYS, { isInternal: queryParams?.isInternal, types, name: queryParams?.name }], enabled, select: sortByName })

  return { ...query, data: query.data ?? NO_BIDDER_SEATS }
}

export const useBidderSeat = (product: Product, bidderSeatId?: string) => {
  return useQuery<BidderSeat>({ url: `/${product}/bidder-seats/${bidderSeatId}`, keys: [product, ...BIDDER_SEATS_QUERY_KEYS, bidderSeatId || ''], enabled: !!bidderSeatId })
}

export const useBidderSeatsByBidderCode = (bidderCode?: string) => {
  const url = buildUrlWithSearchParams('/adm/bidder-seats', bidderCode ? { bidderCode } : {})
  const query = useQuery<BidderSeat[]>({ url, keys: ['adm', ...BIDDER_SEATS_QUERY_KEYS], enabled: !!bidderCode, select: sortByName })

  return { ...query, data: query.data ?? NO_BIDDER_SEATS }
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateBidderSeat = (product: Product, toasts: MutationToasts = {}) => {
  return useMutation<CreateBidderSeat>({
    method: 'post',
    url: `/${product}/bidder-seats`,
    collectionKeys: [product, ...BIDDER_SEATS_QUERY_KEYS],
    ...toasts,
  })
}

export const useUpdateBidderSeat = (product: Product, id: string, toasts: MutationToasts = {}) => {
  return useMutation<CreateBidderSeat>({
    method: 'put',
    url: `/${product}/bidder-seats/${id}`,
    collectionKeys: [product, ...BIDDER_SEATS_QUERY_KEYS],
    resourceId: id,
    ...toasts,
  })
}

export const useDeleteBidderSeat = (product: Product, id: string, toasts: MutationToasts = {}) => {
  return useMutation<void>({
    method: 'delete',
    url: `/${product}/bidder-seats/${id}`,
    collectionKeys: [product, ...BIDDER_SEATS_QUERY_KEYS],
    resourceId: id,
    ...toasts,
  })
}
